import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import {
  icons,
  MessageSquare,
  MoreHorizontal,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChatInput } from "~/components/chat/chat-input";
import { TemplateBar } from "~/components/chat/dashboard-template-bar";
import type { PromptTemplate } from "~/components/chat/new-chat";
import { DashboardSkeleton } from "~/components/loading-skeleton";
import { SyncStatusBadge } from "~/components/sync/sync-status-badge";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Avatar, AvatarFallback } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useImageDropZone } from "~/hooks/use-image-drop-zone";
import { useInitialChatModel } from "~/hooks/use-initial-chat-model";
import { createLocalChatSession, deleteLocalChat } from "~/lib/chat/local-mutations";
import {
  useLocalChatCount,
  useLocalMessageCount,
  useLocalRecentChats,
} from "~/lib/chat/local-queries";
import { useCommandPalette } from "~/lib/command-palette/context";
import { getStorageQuota } from "~/lib/db/local-db";
import { toLocalDbError } from "~/lib/db/local-errors";
import { $getAiProviderConnections, $getPromptTemplates } from "~/lib/queries";
import { cn } from "~/lib/utils";

type TemplateWithIcon = PromptTemplate & {
  IconComponent: React.ElementType;
  colors: {
    bg: string;
    text: string;
    border: string;
    hoverBg: string;
  };
};

const colorMap: Record<
  string,
  { bg: string; text: string; border: string; hoverBg: string }
> = {
  blue: {
    bg: "bg-blue-50 dark:bg-blue-950/30",
    text: "text-blue-600 dark:text-blue-400",
    border: "border-blue-200 dark:border-blue-800",
    hoverBg: "hover:bg-blue-50 dark:hover:bg-blue-950/50",
  },
  green: {
    bg: "bg-green-50 dark:bg-green-950/30",
    text: "text-green-600 dark:text-green-400",
    border: "border-green-200 dark:border-green-800",
    hoverBg: "hover:bg-green-50 dark:hover:bg-green-950/50",
  },
  purple: {
    bg: "bg-purple-50 dark:bg-purple-950/30",
    text: "text-purple-600 dark:text-purple-400",
    border: "border-purple-200 dark:border-purple-800",
    hoverBg: "hover:bg-purple-50 dark:hover:bg-purple-950/50",
  },
  orange: {
    bg: "bg-orange-50 dark:bg-orange-950/30",
    text: "text-orange-600 dark:text-orange-400",
    border: "border-orange-200 dark:border-orange-800",
    hoverBg: "hover:bg-orange-50 dark:hover:bg-orange-950/50",
  },
  red: {
    bg: "bg-red-50 dark:bg-red-950/30",
    text: "text-red-600 dark:text-red-400",
    border: "border-red-200 dark:border-red-800",
    hoverBg: "hover:bg-red-50 dark:hover:bg-red-950/50",
  },
  pink: {
    bg: "bg-pink-50 dark:bg-pink-950/30",
    text: "text-pink-600 dark:text-pink-400",
    border: "border-pink-200 dark:border-pink-800",
    hoverBg: "hover:bg-pink-50 dark:hover:bg-pink-950/50",
  },
  yellow: {
    bg: "bg-yellow-50 dark:bg-yellow-950/30",
    text: "text-yellow-600 dark:text-yellow-400",
    border: "border-yellow-200 dark:border-yellow-800",
    hoverBg: "hover:bg-yellow-50 dark:hover:bg-yellow-950/50",
  },
  cyan: {
    bg: "bg-cyan-50 dark:bg-cyan-950/30",
    text: "text-cyan-600 dark:text-cyan-400",
    border: "border-cyan-200 dark:border-cyan-800",
    hoverBg: "hover:bg-cyan-50 dark:hover:bg-cyan-950/50",
  },
};

function getIconName(name: string | null): string {
  if (!name) return "Zap";
  const pascalName = name
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
  return pascalName in icons ? pascalName : "Zap";
}

export const Route = createFileRoute("/(authenticated)/dashboard/")({
  component: DashboardIndex,
  pendingComponent: DashboardSkeleton,
  wrapInSuspense: true,
});

function DashboardIndex() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = Route.useRouteContext();
  const { open } = useCommandPalette();

  const { data: templates } = useQuery({
    queryKey: ["prompt-templates"],
    queryFn: () => $getPromptTemplates(),
  });

  const recentChats = useLocalRecentChats(15);
  const localChatCount = useLocalChatCount();
  const localMessageCount = useLocalMessageCount();

  const { data: providerConnections } = useQuery({
    queryKey: ["ai-provider-connections"],
    queryFn: () => $getAiProviderConnections(),
  });

  // Chat input state
  const [input, setInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    model,
    setModel,
    providerId,
    setProviderId,
  } = useInitialChatModel();
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | null>(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [chatInputKey, setChatInputKey] = useState(0);
  const imageHandlerRef = useRef<((file: File) => void) | null>(null);

  const [quota, setQuota] = useState<Awaited<ReturnType<typeof getStorageQuota>>>(null);
  const quotaWarnedRef = useRef(false);

  // Image drop zone
  const { dragProps } = useImageDropZone({
    onImageDrop: (files) => {
      files.forEach((file) => {
        if (imageHandlerRef.current) {
          imageHandlerRef.current(file);
        }
      });
    },
  });

  useEffect(() => {
    let active = true;

    getStorageQuota()
      .then((q) => {
        if (!active) return;
        setQuota(q);
      })
      .catch(() => {
        if (!active) return;
        setQuota(null);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!quota) return;
    if (quotaWarnedRef.current) return;
    if (quota.percentage < 80) return;

    quotaWarnedRef.current = true;
    console.warn("[STORAGE] Local storage usage high:", quota);
  }, [quota]);

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!input.trim() || isSubmitting || isUploadingImages) return;

    const messageToSend = selectedTemplate?.userPromptPrefix
      ? `${selectedTemplate.userPromptPrefix}${input}`
      : input;

    const imagesToSend = imageUrls;
    const templateToUse = selectedTemplate;

    setIsSubmitting(true);

    try {
      const session = await createLocalChatSession({
        templateId: templateToUse?.id || null,
        title: messageToSend.slice(0, 50) + (messageToSend.length > 50 ? "..." : ""),
        model,
        providerId,
      });

      queryClient.setQueryData(["pending-message", session.id], {
        content: messageToSend,
        model,
        providerId,
        systemPrompt: templateToUse?.systemPrompt ?? undefined,
        imageUrls: imagesToSend.length > 0 ? imagesToSend : undefined,
      });

      // Clear draft now that we have a session
      setInput("");
      setSelectedTemplate(null);
      setImageUrls([]);
      setChatInputKey((k) => k + 1);

      navigate({
        to: "/dashboard/chat/$chatId",
        params: { chatId: session.id },
      });
    } catch (error) {
      console.error("[DASHBOARD] Failed to create chat:", error);

      const localError = toLocalDbError(error);
      if (localError.code === "quota_exceeded") {
        toast.error("Local storage full", {
          description: localError.message,
          action: {
            label: "Sync settings",
            onClick: () => navigate({ to: "/dashboard/settings/sync" }),
          },
          duration: 10000,
        });
      } else {
        toast.error("Failed to create chat", {
          description: localError.message,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show top templates (sorted by usage, then favorites)
  const topTemplates = useMemo<TemplateWithIcon[]>(() => {
    return (
      templates
        ?.sort((a, b) => {
          // Favorites first
          if (a.isFavorite && !b.isFavorite) return -1;
          if (!a.isFavorite && b.isFavorite) return 1;
          // Then by usage count
          return (b.usageCount || 0) - (a.usageCount || 0);
        })
        .slice(0, 15)
        .map((template) => {
          const IconComponent = icons[
            getIconName(template.icon) as keyof typeof icons
          ] as React.ElementType;
          return {
            ...template,
            IconComponent: IconComponent || icons.Zap,
            colors: colorMap[template.color ?? "blue"] ?? colorMap.blue,
          };
        }) || []
    );
  }, [templates]);

  const quotaIsHigh = (quota?.percentage ?? 0) >= 80;
  const activeProviders = providerConnections?.filter((p) => p.isActive).length ?? 0;

  const stats = {
    totalChats: localChatCount,
    totalTemplates: templates?.length ?? 0,
    totalMessages: localMessageCount,
    activeProviders,
  };

  return (
    <div
      className="mx-auto flex w-full max-w-4xl flex-col gap-6 pb-6 md:gap-8 xl:max-w-5xl 2xl:max-w-6xl"
      {...dragProps}
    >
      {/* Top Section - Chat Input */}
      <div className="flex flex-col gap-4 pt-2 md:gap-5">
        {quotaIsHigh && (
          <div className="px-2 md:px-4">
            <Alert>
              <AlertTitle>Local storage almost full</AlertTitle>
              <AlertDescription>
                Consider syncing chats you want to keep, then deleting old local chats.
                You can manage sync settings in{" "}
                <Link
                  to="/dashboard/settings/sync"
                  className="underline underline-offset-3"
                >
                  Sync Settings
                </Link>
                .
              </AlertDescription>
            </Alert>
          </div>
        )}

        <div className="text-center md:px-4 md:text-left">
          <h1 className="text-xl font-semibold tracking-tight md:text-2xl">
            Good {getTimeOfDay()}, {user.name.split(" ")[0]}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">
            What would you like to work on today?
          </p>
        </div>

        {/* Template Bar */}
        <div className="px-2 md:px-4">
          <TemplateBar
            templates={topTemplates}
            onSelectTemplate={(template) => {
              setSelectedTemplate(template);
              if (template.defaultModel) {
                setModel(template.defaultModel);
              }
              if (template.defaultProviderId !== undefined) {
                setProviderId(template.defaultProviderId);
              }
            }}
            selectedTemplateId={selectedTemplate?.id}
          />
        </div>

        {/* Chat Input - Same as chat page */}
        <div className="px-2 md:px-4">
          <ChatInput
            key={chatInputKey}
            input={input}
            setInput={setInput}
            handleSubmit={handleSubmit}
            isLoading={isSubmitting}
            placeholder={
              selectedTemplate?.userPromptPrefix
                ? `${selectedTemplate.name}: Enter your ${selectedTemplate.name.toLowerCase()} request...`
                : "Ask anything..."
            }
            model={model}
            providerId={providerId}
            onModelChange={setModel}
            onProviderChange={setProviderId}
            onImagesChange={setImageUrls}
            onUploadingChange={setIsUploadingImages}
            onImageHandlerReady={(handler) => {
              imageHandlerRef.current = handler;
            }}
          />
        </div>

        {/* Command palette hint */}
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={open}
            className="text-muted-foreground hover:text-foreground text-xs"
          >
            <Sparkles className="mr-1.5 size-3" />
            Press ⌘K for commands
          </Button>
        </div>
      </div>

      {/* Recent Chats Section */}
      <div className="space-y-3 px-2 md:px-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-tight">Recent Chats</h2>
          <Link to="/dashboard/chat/new">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              <Plus className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">New Chat</span>
            </Button>
          </Link>
        </div>

        {recentChats === undefined ? null : recentChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="bg-muted/50 mb-4 flex h-12 w-12 items-center justify-center rounded-full">
              <MessageSquare className="text-muted-foreground h-6 w-6" />
            </div>
            <h3 className="text-lg font-semibold">No chats yet</h3>
            <p className="text-muted-foreground mb-4 max-w-sm text-sm">
              Start a new conversation to get help with your tasks, code, or writing.
            </p>
            <Button onClick={open}>Start Your First Chat</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {recentChats.map((chat) => (
              <ChatListItem key={chat.id} chat={chat} />
            ))}
          </div>
        )}
      </div>

      {/* Stats Section - Compact at Bottom */}
      <div className="pt-2">
        <StatsCompact stats={stats} />
      </div>
    </div>
  );
}

function ChatListItem({
  chat,
}: {
  chat: {
    id: string;
    title: string;
    isPinned: boolean;
    isSynced: boolean;
    updatedAt: Date;
    messageCount: number;
    firstMessagePreview: string | null;
  };
}) {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (confirm("Delete this chat?")) {
      setIsDeleting(true);
      try {
        await deleteLocalChat(chat.id);
        toast.success("Chat deleted");
      } catch (error) {
        console.error("Failed to delete chat:", error);
        const localError = toLocalDbError(error);
        toast.error("Failed to delete chat", {
          description: localError.message,
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleClick = () => {
    navigate({
      to: "/dashboard/chat/$chatId",
      params: { chatId: chat.id },
    });
  };

  const previewText = chat.firstMessagePreview
    ? chat.firstMessagePreview.slice(0, 80) +
      (chat.firstMessagePreview.length > 80 ? "..." : "")
    : "No messages";

  return (
    <div
      onClick={handleClick}
      className={cn(
        "group hover:bg-muted/50 border-border/50 hover:border-primary/30 flex cursor-pointer flex-col gap-2 rounded-lg border p-4 transition-all hover:shadow-sm",
        isDeleting && "opacity-50",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Avatar className="bg-muted h-8 w-8 shrink-0 rounded-md">
            <AvatarFallback className="text-muted-foreground rounded-md">
              <MessageSquare className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
          <h3 className="truncate text-sm font-medium">{chat.title}</h3>
        </div>

        <div className="flex items-center gap-1">
          <SyncStatusBadge isSynced={chat.isSynced} />
          <DropdownMenu>
            <DropdownMenuTrigger
              render={(props) => (
                <Button
                  {...props}
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              )}
            />
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <p className="text-muted-foreground line-clamp-2 text-xs">{previewText}</p>

      <div className="text-muted-foreground flex items-center justify-between text-xs">
        <span className="tabular-nums">
          {chat.messageCount} {chat.messageCount === 1 ? "msg" : "msgs"}
        </span>
        <span>{formatDistanceToNow(new Date(chat.updatedAt), { addSuffix: true })}</span>
      </div>
    </div>
  );
}

function StatsCompact({
  stats,
}: {
  stats?: {
    totalChats: number;
    totalTemplates: number;
    totalMessages: number;
    activeProviders: number;
  };
}) {
  if (!stats) return null;

  const items = [
    { label: "Chats", value: stats.totalChats },
    { label: "Templates", value: stats.totalTemplates },
    { label: "Messages", value: stats.totalMessages },
    { label: "Providers", value: stats.activeProviders },
  ];

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 text-xs">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-1.5">
          <span className="text-muted-foreground">{item.label}</span>
          <span className="font-medium">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

function getTimeOfDay(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "morning";
  if (hour < 17) return "afternoon";
  return "evening";
}
